import { ShiftRequest } from 'types/shift-requests';
import { parseJobCodeId } from './jobCodeEligibility';

const extractShiftId = (shiftRef: unknown): number | null => {
	if (shiftRef == null || shiftRef === '') return null;
	if (typeof shiftRef === 'number' && Number.isFinite(shiftRef)) return shiftRef;
	if (typeof shiftRef === 'string') {
		const match = shiftRef.match(/(\d+)$/);
		return match ? Number(match[1]) : null;
	}
	if (typeof shiftRef === 'object') {
		const shift = shiftRef as { id?: number | string; '@id'?: string };
		if (typeof shift.id === 'number' && Number.isFinite(shift.id)) return shift.id;
		if (shift.id != null) return extractShiftId(String(shift.id));
		if (shift['@id']) return extractShiftId(shift['@id']);
	}
	return null;
};

const toPickupShiftOption = (shift: unknown) => {
	if (!shift || typeof shift !== 'object') return null;
	const option = shift as Record<string, any>;
	const id = extractShiftId(option);
	if (id == null) return null;

	return {
		...option,
		id,
		startAt: option.startAt || option.start_at || option.start,
		endAt: option.endAt || option.end_at || option.end,
	};
};

export const getPickupRequestFormValues = (
	editRequest?: Pick<ShiftRequest, 'shiftRequest' | 'note'> | null
) => ({
	shiftId: extractShiftId(editRequest?.shiftRequest)?.toString() || '',
	notes: editRequest?.note || '',
});

export const mergePickupShiftOptions = (
	houseShifts: any[] = [],
	selectedShift: unknown,
	selectedShiftId?: string | number | null
) => {
	if (selectedShiftId == null || selectedShiftId === '') return houseShifts;

	const selectedId = String(selectedShiftId);
	const extra = toPickupShiftOption(selectedShift);
	if (!extra) return houseShifts;

	const remaining = houseShifts.filter(
		(shift) => String(shift.id) !== selectedId && String(extractShiftId(shift)) !== selectedId
	);

	return [extra, ...remaining];
};

/**
 * Keep only house shifts whose job code the employee is eligible to work.
 * `alwaysIncludeShiftId` keeps an in-progress edit selection visible even if
 * eligibility no longer includes that job code.
 */
export const filterPickupShiftsByEligibleJobCodes = (
	shifts: any[] = [],
	eligibleJobCodeIds: Iterable<number> | null | undefined,
	options?: { alwaysIncludeShiftId?: string | number | null }
): any[] => {
	const eligible = new Set(
		eligibleJobCodeIds == null ? [] : Array.from(eligibleJobCodeIds).filter((id) => Number.isFinite(id))
	);
	const includeId =
		options?.alwaysIncludeShiftId != null && options.alwaysIncludeShiftId !== ''
			? String(options.alwaysIncludeShiftId)
			: null;

	return shifts.filter((shift) => {
		if (includeId != null) {
			const shiftId = extractShiftId(shift);
			if (shiftId != null && String(shiftId) === includeId) return true;
		}
		const jobCodeId = parseJobCodeId(shift?.jobCode);
		if (jobCodeId == null) return false;
		return eligible.has(jobCodeId);
	});
};
